import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import AnimusHeroChrome from "../components/AnimusHeroChrome";
import { apiGet } from "../api/client";
import { getVideoCategory, sortVideosByDate, VIDEO_CATEGORIES, VIDEO_TYPE_LABELS } from "../utils/videoCategories";
import "../styles.css";

const VIDEOS_PER_PAGE = 12;

export default function Videos() {
  const [videoData, setVideoData] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    apiGet('/videos')
      .then(setVideoData)
      .catch((e) => setFetchError(e.message))
      .finally(() => setFetchLoading(false));
  }, []);

  const [visibleCount, setVisibleCount] = useState(VIDEOS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Videos");

  const sortedVideos = useMemo(() => {
    const since2020 = videoData.filter((video) => {
      const year = new Date(video.snippet.publishedAt).getFullYear();
      return year >= 2020;
    });
    return sortVideosByDate(since2020);
  }, [videoData]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    sortedVideos.forEach((video) => {
      const category = getVideoCategory(video);
      counts[category] = (counts[category] || 0) + 1;
    });
    return counts;
  }, [sortedVideos]);

  const categories = [
    "All Videos",
    ...VIDEO_CATEGORIES.filter((cat) => categoryCounts[cat]),
    ...Object.keys(categoryCounts).filter((cat) => !VIDEO_CATEGORIES.includes(cat)),
  ];

  const displayVideos = useMemo(() => {
    let list = sortedVideos;
    if (selectedCategory !== "All Videos") {
      list = list.filter(
        (video) => getVideoCategory(video) === selectedCategory,
      );
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter((video) =>
        video.snippet.title.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sortedVideos, selectedCategory, searchTerm]);

  const paginatedVideos = displayVideos.slice(0, visibleCount);

  function handleLoadMore() {
    setVisibleCount((v) => v + VIDEOS_PER_PAGE);
  }

  return (
    <>
      <SEO
        title="YouTube Videos"
        description="Watch ZeMaxi's YouTube videos including gaming tutorials, Streamer.bot guides, OBS setup walkthroughs, and gameplay highlights. Learn streaming tips and tricks!"
        keywords="ZeMaxi YouTube, streaming tutorials, Streamer.bot tutorials, OBS guides, gaming videos, gameplay highlights, streaming tips"
        url="https://zemaxi.com/videos"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="videos-hero animus-hero animus-hero-bg">
          <AnimusHeroChrome />
          <div className="container">
            <p className="animus-hero-id font-mono">{'// SECTION_VIDEOS'}</p>
            <h1>YouTube <span className="highlight">Videos</span></h1>
            <p>Watch my latest YouTube videos and highlights!</p>
          </div>
        </section>
        <section className="videos-section page-body-section">
          <div className="container videos-container">
            <div className="games-search">
              <div className="search-filter-row">
                <div className="search-container">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    placeholder="Search videos..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(VIDEOS_PER_PAGE); }}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="search-clear"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="videos-category-tabs" role="tablist" aria-label="Video categories">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    role="tab"
                    aria-selected={selectedCategory === cat}
                    className={`videos-category-tab${selectedCategory === cat ? " is-active" : ""}`}
                    onClick={() => { setSelectedCategory(cat); setVisibleCount(VIDEOS_PER_PAGE); }}
                  >
                    {cat}
                    <span className="videos-category-count">
                      {cat === "All Videos" ? sortedVideos.length : categoryCounts[cat]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            {fetchLoading && (
              <div className="video-loading">
                <div className="spinner"></div>
                <p>Loading videos...</p>
              </div>
            )}
            {fetchError && (
              <div className="video-loading">
                <p style={{ color: 'red' }}>Failed to load videos: {fetchError}</p>
              </div>
            )}
            {!fetchLoading && !fetchError && displayVideos.length === 0 && (
              <div className="video-loading">
                <p>No videos match this filter.</p>
              </div>
            )}
            {!fetchLoading && !fetchError && displayVideos.length > 0 && (
              <>
                <div className="video-grid">
                  {paginatedVideos.map(video => (
                    <div className="video-item" key={video.id.videoId}>
                      <a href={`https://www.youtube.com/watch?v=${video.id.videoId}`} target="_blank" rel="noopener noreferrer">
                        <div className="video-thumbnail">
                          <img src={video.snippet.thumbnails.high.url} alt={video.snippet.title} loading="lazy" />
                          {video.type !== "video" && (
                            <span className={`video-type-badge video-type-badge--${video.type}`}>
                              {VIDEO_TYPE_LABELS[video.type]}
                            </span>
                          )}
                        </div>
                        <div className="video-info">
                          <h3>{video.snippet.title}</h3>
                          <div className="video-meta">
                            <span>{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
                {paginatedVideos.length < displayVideos.length && (
                  <div className="load-more-container">
                    <button className="btn btn-primary" onClick={handleLoadMore}>Load More</button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
